import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ChefHat, 
  Search, 
  Filter,
  Plus,
  Edit,
  Trash2,
  Calculator,
  Package
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useFilteredBranches } from '@/hooks/useFilteredBranches';
import { RecipeManager } from './RecipeManager';

interface MenuItem {
  id: string;
  name: string;
  category_id: string;
  base_price: number;
  description?: string;
}

interface Recipe {
  id: string;
  menu_item_id: string;
  name: string;
  description: string;
  portion_size: number;
  ingredients: Array<{
    id: string;
    material_id: string;
    quantity: number;
    unit: string;
    material_name: string;
    cost_per_unit: number;
  }>;
}

interface RecipeManagementPageProps {
  selectedBranchId: string;
}

export const RecipeManagementPage: React.FC<RecipeManagementPageProps> = ({ selectedBranchId }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { branches } = useFilteredBranches();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedMenuItem, setSelectedMenuItem] = useState<MenuItem | null>(null);
  const dataLoadedRef = useRef(false);



  // Load data only when user and branch are available, and only once
  useEffect(() => {
    if (user && selectedBranchId && !dataLoadedRef.current) {
      if (import.meta.env.DEV) {
        console.log('🔄 Triggering data load for branch:', selectedBranchId);
      }
      loadData();
      dataLoadedRef.current = true;
    }
  }, [user, selectedBranchId]);

  // Reset data loaded flag when branch changes
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('🔄 Branch changed, resetting data loaded flag');
    }
    dataLoadedRef.current = false;
  }, [selectedBranchId]);

  const loadData = async () => {
    if (!user || !selectedBranchId) return;
    
    if (import.meta.env.DEV) {
      console.log('🔄 Loading data for branch:', selectedBranchId);
    }
    
    setLoading(true);
    try {
      // Step 1: Load categories for the selected branch
      const { data: categoriesData, error: categoriesError } = await (supabase as any)
        .from('categories')
        .select('id, name')
        .eq('branch_id', selectedBranchId)
        .order('name');

      if (import.meta.env.DEV) {
        console.log('📂 Categories loaded for branch', selectedBranchId, ':', categoriesData?.length || 0);
      }

      if (categoriesError) {
        console.error('Error loading categories:', categoriesError);
        setCategories([]);
      } else {
        setCategories(categoriesData || []);
      }

      // Step 2: Load menu items for the categories we just loaded
      let menuItemsData = [];
      if (categoriesData && categoriesData.length > 0) {
        const categoryIds = categoriesData.map(c => c.id);
        if (import.meta.env.DEV) {
          console.log('🍽️ Loading menu items for categories:', categoryIds);
        }
        
        try {
          // Simplified query to avoid 406 errors
          const { data: itemsData, error: menuItemsError } = await (supabase as any)
            .from('menu_items')
            .select('id, name, category_id, base_price, description')
            .in('category_id', categoryIds)
            .order('name');

          if (import.meta.env.DEV) {
            console.log('🍽️ Branch-specific menu items loaded:', itemsData?.length || 0);
          }

          if (menuItemsError) {
            console.error('Error loading menu items:', menuItemsError);
          } else {
            menuItemsData = itemsData || [];
            if (import.meta.env.DEV) {
              console.log('🍽️ Final menu items count:', menuItemsData.length);
              console.log('🍽️ Sample menu items:', menuItemsData.slice(0, 3));
            }
          }
        } catch (error) {
          console.error('Exception loading menu items:', error);
        }
      }
      
      setMenuItems(menuItemsData);

      // Step 3: Load recipes with ingredients and material details
      const { data: recipesData, error: recipesError } = await (supabase as any)
        .from('recipes')
        .select(`
          id,
          menu_item_id,
          name,
          description,
          portion_size,
          recipe_ingredients (
            id,
            material_id,
            quantity,
            unit,
            raw_materials (
              name,
              unit_type,
              cost_per_unit
            )
          )
        `)
        .eq('user_id', user.id);

      if (recipesError) {
        console.error('Error loading recipes:', recipesError);
      } else {
        // Transform the data to match the expected format with material details
        const transformedRecipes = (recipesData || []).map(recipe => ({
          ...recipe,
          ingredients: (recipe.recipe_ingredients || []).map(ingredient => ({
            id: ingredient.id,
            material_id: ingredient.material_id,
            quantity: ingredient.quantity,
            unit: ingredient.unit,
            material_name: ingredient.raw_materials?.name || 'Unknown Material',
            cost_per_unit: ingredient.raw_materials?.cost_per_unit || 0
          }))
        }));
        setRecipes(transformedRecipes);
        if (import.meta.env.DEV) {
          console.log('📋 Recipes loaded with ingredients:', transformedRecipes.length);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error loading data',
        description: 'Failed to load menu items and recipes',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredMenuItems = menuItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || !selectedCategory || item.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getRecipeForMenuItem = (menuItemId: string) => {
    return recipes.find(recipe => recipe.menu_item_id === menuItemId);
  };

  const calculateRecipeCost = (recipe: Recipe) => {
    return recipe.ingredients.reduce((total, ing) => total + (ing.quantity * ing.cost_per_unit), 0);
  };

  const handleRecipeUpdated = () => {
    dataLoadedRef.current = false; // Reset the flag to allow reloading
    loadData();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <ChefHat className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-500">Loading recipes...</p>
        </div>
      </div>
    );
  }

  if (!selectedBranchId) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <ChefHat className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {branches.length > 0 ? 'Select a branch to manage recipes' : 'No branches available'}
          </h3>
          <p className="text-gray-600">
            {branches.length > 0 
              ? 'Choose a branch from the dropdown to start managing recipes for its menu items.'
              : 'Create a branch first to start managing recipes.'
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 truncate">Recipe Management</h1>
          <p className="text-xs md:text-sm text-gray-600 mt-1 line-clamp-2">
            Create and manage recipes for your menu items to track inventory and costs
          </p>
          {selectedBranchId && (
            <p className="text-xs md:text-sm text-blue-600 mt-1 truncate">
              📍 Currently viewing: <strong>{branches.find(b => b.id === selectedBranchId)?.name}</strong>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
          <Badge variant="outline" className="flex items-center gap-1 text-xs">
            <ChefHat className="h-3 w-3" />
            {recipes.length} Recipes
          </Badge>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 md:border shadow-sm">
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2.5 md:left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 md:h-4 md:w-4 text-gray-400" />
                <Input
                  placeholder="Search menu items..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 md:pl-10 text-sm h-9 md:h-10"
                />
              </div>
            </div>
            <div className="w-full sm:w-48">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="text-sm h-9 md:h-10">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-sm">All categories</SelectItem>
                  {categories.map(category => (
                    <SelectItem key={category.id} value={category.id} className="text-sm">
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recipe List */}
      <div className="grid gap-3 md:gap-4 lg:gap-6">
        {filteredMenuItems.length === 0 ? (
          <Card className="border-0 md:border shadow-sm">
            <CardContent className="text-center py-8 md:py-12 px-4">
              <ChefHat className="h-12 w-12 md:h-16 md:w-16 text-gray-400 mx-auto mb-3 md:mb-4" />
              <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2">
                No menu items found
              </h3>
              <p className="text-sm md:text-base text-gray-600 line-clamp-3">
                {search || selectedCategory !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : `No menu items found in ${branches.find(b => b.id === selectedBranchId)?.name || 'this branch'}. Add menu items to start creating recipes.`
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredMenuItems.map(menuItem => {
            const recipe = getRecipeForMenuItem(menuItem.id);
            const category = categories.find(c => c.id === menuItem.category_id);
            
            return (
              <Card key={menuItem.id} className="overflow-hidden border-0 md:border shadow-sm">
                <CardHeader className="pb-3 md:pb-4 p-3 md:p-4 lg:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                        <span className="truncate">{menuItem.name}</span>
                        {recipe && (
                          <Badge variant="secondary" className="flex items-center gap-1 text-xs flex-shrink-0">
                            <ChefHat className="h-3 w-3" />
                            Recipe
                          </Badge>
                        )}
                      </CardTitle>
                      <div className="flex flex-wrap items-center gap-2 md:gap-4 mt-1.5 md:mt-2 text-xs md:text-sm text-gray-600">
                        {category && (
                          <span className="truncate">{category.name}</span>
                        )}
                        <span className="flex-shrink-0">${menuItem.base_price.toFixed(2)}</span>
                        {recipe && (
                          <span className="flex items-center gap-1 flex-shrink-0">
                            <Calculator className="h-3 w-3" />
                            Cost: ${calculateRecipeCost(recipe).toFixed(2)}
                          </span>
                        )}
                      </div>
                      {menuItem.description && (
                        <p className="text-xs md:text-sm text-gray-500 mt-1 line-clamp-2">{menuItem.description}</p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedMenuItem(menuItem)}
                      className="w-full sm:w-auto text-xs md:text-sm flex-shrink-0"
                    >
                      {recipe ? (
                        <>
                          <Edit className="h-3 w-3 md:h-4 md:w-4 mr-1.5 md:mr-2" />
                          Edit Recipe
                        </>
                      ) : (
                        <>
                          <Plus className="h-3 w-3 md:h-4 md:w-4 mr-1.5 md:mr-2" />
                          Create Recipe
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                
                {recipe && (
                  <CardContent className="pt-0 p-3 md:p-4 lg:p-6">
                    <div className="border-t pt-3 md:pt-4">
                      <h4 className="font-medium mb-2 md:mb-3 text-sm md:text-base">Ingredients:</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {recipe.ingredients.map((ingredient, index) => (
                          <div key={index} className="flex items-center justify-between text-xs md:text-sm p-2 bg-gray-50 rounded gap-2">
                            <span className="font-medium truncate flex-1">{ingredient.material_name}</span>
                            <span className="text-gray-600 flex-shrink-0 whitespace-nowrap">
                              {ingredient.quantity} {ingredient.unit}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })
        )}
      </div>

      {/* Recipe Dialog */}
      {selectedMenuItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Recipe for {selectedMenuItem.name}</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedMenuItem(null)}
                >
                  ×
                </Button>
              </div>
              <RecipeManager
                menuItem={selectedMenuItem}
                onRecipeUpdated={handleRecipeUpdated}
                selectedBranchId={selectedBranchId}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 